import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { claimsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CLAIM_TYPES } from '@/utils/constants';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const SubmitClaimPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    policy_number: '',
    insured_name: '',
    incident_date: '',
    incident_time: '',
    incident_location: '',
    claim_type: '',
    description: '',
    estimated_loss: '',
    provider_name: '',
    admission_date: '',
    discharge_date: '',
    attendant_practitioner: '',
    remarks: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        estimated_loss: parseFloat(formData.estimated_loss),
      };
      const response = await claimsAPI.create(dataToSubmit);
      toast.success('Claim submitted successfully!');
      navigate(`/claims/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Submit New Claim
        </h1>
        <p className="text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
          First Notice of Loss (FNOL) - Please provide detailed information about your claim
        </p>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Claim Information</CardTitle>
          <CardDescription style={{ fontFamily: 'Public Sans, sans-serif' }}>
            All fields marked with * are required
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="policy_number" style={{ fontFamily: 'Public Sans, sans-serif' }}>Policy/ID Number *</Label>
                <Input
                  id="policy_number"
                  name="policy_number"
                  data-testid="policy-number-input"
                  placeholder="POL-2026-XXXXX"
                  value={formData.policy_number}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="insured_name" style={{ fontFamily: 'Public Sans, sans-serif' }}>Insured / Patient Name *</Label>
                <Input
                  id="insured_name"
                  name="insured_name"
                  data-testid="insured-name-input"
                  placeholder="John Doe"
                  value={formData.insured_name}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="incident_date" style={{ fontFamily: 'Public Sans, sans-serif' }}>Incident Date *</Label>
                <Input
                  id="incident_date"
                  name="incident_date"
                  data-testid="incident-date-input"
                  type="date"
                  value={formData.incident_date}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="incident_time" style={{ fontFamily: 'Public Sans, sans-serif' }}>Incident Time *</Label>
                <Input
                  id="incident_time"
                  name="incident_time"
                  data-testid="incident-time-input"
                  type="time"
                  value={formData.incident_time}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="incident_location" style={{ fontFamily: 'Public Sans, sans-serif' }}>Incident Location *</Label>
              <Input
                id="incident_location"
                name="incident_location"
                data-testid="incident-location-input"
                placeholder="Street address, City, State, ZIP"
                value={formData.incident_location}
                onChange={handleChange}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="claim_type" style={{ fontFamily: 'Public Sans, sans-serif' }}>Claim Type *</Label>
                <Select
                  value={formData.claim_type}
                  onValueChange={(value) => setFormData({ ...formData, claim_type: value })}
                  required
                >
                  <SelectTrigger data-testid="claim-type-select" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                    <SelectValue placeholder="Select claim type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLAIM_TYPES.map((type) => (
                      <SelectItem key={type} value={type} data-testid={`claim-type-option-${type.toLowerCase().replace(/\s+/g, '-')}`}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimated_loss" style={{ fontFamily: 'Public Sans, sans-serif' }}>Established Loss Amount / Bill (₦) *</Label>
                <Input
                  id="estimated_loss"
                  name="estimated_loss"
                  data-testid="estimated-loss-input"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.estimated_loss}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="provider_name" style={{ fontFamily: 'Public Sans, sans-serif' }}>Name of Provider</Label>
                <Input
                  id="provider_name"
                  name="provider_name"
                  data-testid="provider-name-input"
                  placeholder="Hospital/Clinic Name"
                  value={formData.provider_name}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="attendant_practitioner" style={{ fontFamily: 'Public Sans, sans-serif' }}>Name of Attendant/Practitioner</Label>
                <Input
                  id="attendant_practitioner"
                  name="attendant_practitioner"
                  data-testid="attendant-practitioner-input"
                  placeholder="Dr. John Smith"
                  value={formData.attendant_practitioner}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="admission_date" style={{ fontFamily: 'Public Sans, sans-serif' }}>Date of Admission</Label>
                <Input
                  id="admission_date"
                  name="admission_date"
                  data-testid="admission-date-input"
                  type="date"
                  value={formData.admission_date}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discharge_date" style={{ fontFamily: 'Public Sans, sans-serif' }}>Date of Discharge</Label>
                <Input
                  id="discharge_date"
                  name="discharge_date"
                  data-testid="discharge-date-input"
                  type="date"
                  value={formData.discharge_date}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" style={{ fontFamily: 'Public Sans, sans-serif' }}>Description *</Label>
              <Textarea
                id="description"
                name="description"
                data-testid="description-textarea"
                placeholder="Please provide detailed information about what happened, including circumstances, damages, injuries, and any other relevant details..."
                value={formData.description}
                onChange={handleChange}
                rows={6}
                required
                style={{ fontFamily: 'Public Sans, sans-serif' }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="remarks" style={{ fontFamily: 'Public Sans, sans-serif' }}>Treatment Details / Any Other Information</Label>
              <Textarea
                id="remarks"
                name="remarks"
                data-testid="remarks-textarea"
                placeholder="Treatment details, medications, additional information or remarks..."
                value={formData.remarks}
                onChange={handleChange}
                rows={4}
                style={{ fontFamily: 'Public Sans, sans-serif' }}
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button 
                type="submit" 
                data-testid="submit-claim-button"
                disabled={loading}
                className="flex-1"
                style={{ fontFamily: 'Public Sans, sans-serif' }}
              >
                {loading ? 'Submitting...' : 'Submit Claim'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate(-1)}
                disabled={loading}
                style={{ fontFamily: 'Public Sans, sans-serif' }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubmitClaimPage;
